import { Flex, FlexProps, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { FormField } from '../../../components/FormField'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { ILeadSource, LeadSourceService } from '../../../services/LeadSource/LeadSourceService'
import { LeadSourcesModal } from './LeadSourcesModal'

interface IProps extends Pick<FlexProps, 'mt'> {
  value: string
  onChange: (source: string) => void
  error?: string | string[]
  isRequired?: boolean
  isDisabled?: boolean
  /** Mostra o botão que abre o "Gerenciar origens" (permissão `manage_lead_sources`). */
  canManage?: boolean
  /** Origem atual de um lead que não está mais entre as gerenciadas (ex.: "Site"), mantida como opção. */
  extraValue?: string
}

/**
 * Campo "Origem" dos leads: as opções vêm das origens gerenciadas no painel. O modal fica fora do
 * `FormField` para as entradas dele não herdarem o estado (id, erro, obrigatório) deste campo.
 */
export function LeadSourceField({ value, onChange, error, isRequired, isDisabled, canManage, extraValue, mt }: IProps) {
  const service = new LeadSourceService()
  const toast = useAppToast()
  const manageModal = useDisclosure()
  const [sources, setSources] = useState<ILeadSource[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const names = sources.map((source) => source.name)
  const options = [...names, ...(extraValue && !names.includes(extraValue) ? [extraValue] : [])]

  const loadSources = async () => {
    setIsLoading(true)

    try {
      const list = await service.list()
      setSources(list)

      return list
    } catch (err) {
      toast.error('Erro ao carregar as origens', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSources()
  }, [])

  return (
    <>
      <FormField label="Origem" error={error} isRequired={isRequired} mt={mt}>
        <Flex gap={2}>
          <FormSelect
            value={value}
            onChange={onChange}
            options={options.map((name) => ({ value: name, label: name }))}
            isLoading={isLoading}
            isDisabled={isDisabled}
          />
          {canManage && (
            <ActionButton
              action="settings"
              label="Gerenciar origens"
              size="md"
              mr={0}
              onClick={manageModal.onOpen}
              isDisabled={isDisabled}
            />
          )}
        </Flex>
      </FormField>

      <LeadSourcesModal
        isOpen={manageModal.isOpen}
        onClose={() => {
          manageModal.onClose()
          loadSources().then((list) => {
            // A origem escolhida pode ter sido excluída no "Gerenciar origens". Só aqui: no primeiro
            // carregamento o `value` ainda pode ser o do lead aberto antes, e limpar apagaria o do atual.
            if (list && value && value !== extraValue && !list.some((source) => source.name === value)) onChange('')
          })
        }}
      />
    </>
  )
}
